/**
 * @remark Store could be anything like user, cart, screen.
 * The first argument is a unique id of the store across your application
 */
import type { ApproachingPromotion, CartStoreContext, FlashError, ShippingAddress } from '#types/cart'
import type { PaymentProvider } from '#types/components/account/creditCard'
import type { Order } from '#types/components/checkout/order'
import type { PaymentMethodCode } from '#types/components/checkout/paymentMethods'
import { ApiErrorCode } from '#root/enums/api'

export const useCheckoutStore = (storeType: CartStoreContext = '', countryCode = useCountryCode()) => defineStore(`checkout${storeType}_${countryCode}`, () => {
  const { $dtrum, $t, $deviceFingerPrint } = useNuxtApp()
  const api = useApi()
  const klarna = useKlarna()
  const toast = useToaster()
  const forterSessionToken = useState('forterSessionToken')

  const auth = useAuthStore()
  const buyInStore = useBuyInStoreStore()
  const cart = useCartStore(storeType)
  const { DialogThreeDsChallenge } = useDialogsStore()
  const approachingPromotions = useApproachingPromotionsStore()
  const { trackEvent } = useOptimizely()

  const paymentProvider = {} as PaymentProvider
  const { sortBonusItemsFirst } = useAppConfig().components.checkout.cartSummary

  const order = ref({} as Order)
  const bin = ref()
  const isOrderFinalized = ref(false)
  const billingAddressRef = ref()
  const isOrderProcessing = ref(false)
  const isExpressCheckout = ref(false)
  // used to set billing address same as shipping address
  const shippingAddress = ref<ShippingAddress>()
  const threeDS2Challenge = ref<string>('')
  // Control variable for Loyalty auto-apply. Once handled (auto-applied or user-changed),
  // we do not auto-apply again during this checkout session.
  const loyaltyAutoApplyHandled = ref(false)

  /**
   * Normalize a placed-order payload for the confirmation page: marks the order finalized,
   * regroups shipments (ship-to-home first, then pickup sorted by delivery time), promotes bonus
   * items per the brand flag, replaces dynamic shipping-method labels with the static UI copy,
   * and flattens 3-part promo `calloutMessage` strings down to the display name. Also pushes the
   * payment type into DynaTrace session properties.
   * @param orderData - raw order payload from `$placeOrder` / `$patchOrder`
   */
  function success(orderData: Order) {
    isOrderFinalized.value = true

    trackEvent('checkout', {
      payment_method: getOptimizelyPaymentMethod(orderData.paymentMethod),
      user_type: auth.loggedIn ? 'logged_in' : 'guest'
    })

    const groupItems = groupItemsByShipment(orderData.items)
    const getSortIndex = (deliveryTime: string) => /same day/i.test(deliveryTime) ? 0 : Number.parseInt(deliveryTime.match(/(\d)-\d/)?.[1] || '1')

    const sthShipments = orderData.shipments.filter(({ shippingMethod }) => !isPickupOrSts(shippingMethod.code))
      .sort(({ shipmentId: a }, { shipmentId: b }) => sortBonusItemsFirst
        ? +groupItems(b).some(({ bonus }) => bonus) - +groupItems(a).some(({ bonus }) => bonus)
        : 0)
    const pickupShipments = orderData.shipments.filter(({ shippingMethod }) => isPickupOrSts(shippingMethod.code))
      .sort(({ shippingMethod: a }, { shippingMethod: b }) =>
        // sort pickup items by deliveryTime
        getSortIndex(a.deliveryTime) - getSortIndex(b.deliveryTime)
      )
    order.value = {
      ...orderData,
      totals: {
        ...orderData.totals,
        totalDiscountPercentage: getPercentage(orderData.totals.itemTotal, orderData.totals.totalDiscount)
      },
      items: orderData.items
        .map((item) => ({
          ...item,
          shippingOptions: item.shippingOptions?.map((option) => ({
            ...option,
            shippingMethod: {
              ...option.shippingMethod,
              // The shipping options label on confirmation page needs to show a static text rather than the label from API response
              label: getShippingMethodLabel($t, option.shippingMethod)
            }
          }))
        }))
        .sort((a, b) => sortBonusItemsFirst
          ? +b.bonus - +a.bonus
          : 0),
      shipments: [
        // put home delivery items before pickup items
        ...sthShipments,
        ...pickupShipments
      ],
      promoSummary: orderData?.promoSummary?.map(
        (promo) => {
          const messages = promo.calloutMessage?.split('|').map((message) => message.trim())
          return ({
            ...promo,
            calloutMessage: messages?.length === 3 ? (promo.promotionName || messages[2]) : promo.calloutMessage
          })
        }
      )
    }

    // DynaTrace Real User Monitoring: Add payment type data to session
    $dtrum.then((dtrum) => {
      dtrum.sendSessionProperties(
        null,
        null,
        {
          payment_type: orderData.paymentMethod?.[0].label
        }
      )
    })
  }

  /**
   * Wipe the in-flight order back to defaults. Does not touch cart state - call sites pair it
   * with `cart.reset()` / `cart.$create({})` when the basket also needs to be replaced.
   */
  function resetOrder() {
    order.value = {} as Order
    isOrderFinalized.value = false
    isOrderProcessing.value = false
    isExpressCheckout.value = false
  }

  function isAuthorized(response) {
    const notAuthorized = response.flash?.find(({ code }) => code === 'PaymentNotAuthorized')
    if (!notAuthorized) return true
    threeDS2Challenge.value = notAuthorized.details?.action || ''
    order.value = response
  }
  /**
   * Submit the cart as an order. Runs payment-method preflight (Klarna authorize, Forter session)
   * + reCAPTCHA, then `$placeOrder`. On `PaymentNotAuthorized` opens the 3DS dialog (skipped for
   * redirect methods P24 / IDEAL). On success normalizes via `success()` and navigates to
   * /order-confirmation. Errors are routed to toast or sticky banner based on `errorId`.
   * @param paymentMethod - payment payload (code, additionalData, Klarna addresses)
   * @param recaptcha - helper from `useRecaptcha`
   * @returns false when 3DS pending, undefined on error, void on success
   */
  async function placeOrder(paymentMethod, recaptcha: ReturnType<typeof useRecaptcha>) {
    isOrderProcessing.value = true
    type OrderBody = {
      cartId: string
      billingAddress?: typeof billingAddressRef.value
      paymentMethod: typeof paymentMethod
      inStoreSalesInfo?: {
        storeId: string
        storeEmployeeId?: string
      }
    }
    const orderBody: OrderBody = {
      cartId: cart.basketId,
      billingAddress: {
        ...billingAddressRef.value,
        phone: billingAddressRef.value.phone
          ? formatE164phone(
              billingAddressRef.value.phone,
              billingAddressRef.value.phoneCode
            )
          : ''
      },
      paymentMethod: {
        ...paymentMethod
      }
    }

    if (buyInStore.storeInfo?.storeId) {
      const { storeId, storeEmployeeId } = buyInStore.storeInfo
      orderBody.inStoreSalesInfo = {
        storeId,
        storeEmployeeId
      }
    }

    // First-time PayPal (no saved instrument id) gets its billing address from the PayPal vendor
    // payload, not from our form - sending one would trigger a server-side validation conflict.
    if (paymentMethod.code === 'PAYPAL' && !paymentMethod.id)
      delete orderBody.billingAddress

    if (paymentMethod.code === 'KLARNA') {
      try {
        const result = await klarna
          .authorize(paymentMethod.billingAddress!, paymentMethod.shippingAddress!)

        orderBody.paymentMethod.additionalData = [result]
      }
      catch (err) {
        // If the actual error happened during the authorization process we need to handle it here
        if (isError(err))
          cart.setNotification('error', $t.apiMessages.PAY400, 'top')

        // If the user closed the Klarna modal we don't want to show any error message
        isOrderProcessing.value = false
        return
      }
    }
    else {
      // add device fingerprint session id to payment details
      orderBody.paymentMethod.additionalData = orderBody.paymentMethod.additionalData.map((obj) => {
        obj.sessionID = forterSessionToken.value || $deviceFingerPrint.getSessionId()
        return obj
      })
      if (bin.value) {
        orderBody.paymentMethod.bin = bin.value.binValue
        orderBody.paymentMethod.type = bin.value.type
      }
    }

    try {
      const captchaResponse = await recaptcha.execute('placeOrder')
      const orderResponse = await api.cart.$placeOrder({
        recaptcha_response: captchaResponse,
        ...orderBody
      }, storeType === 'paypalPdp' ? { bid: 'cookie' } : {})

      const skipThreeDsChallenge = ['P24', 'IDEAL'].includes(paymentMethod.code)
      if (!isAuthorized(orderResponse)) {
        if (!skipThreeDsChallenge) {
          // for an actual 3DS challenge patching the order, we need to trigger the dialog (credit card case)
          // for redirect-type methods, we don't need it, as authorization takes place on the vendor side
          DialogThreeDsChallenge.open({ showTitle: true })
        }
        return false
      }

      try {
        success(orderResponse as Order) // prepare data for confirmation page
        // clear delivery options from session storage when the order is successfully placed
        const deliveryOptions = useSessionStorage('deliveryOptions', null)
        deliveryOptions.value = undefined
        // persist free shipping threshold status for analytics before resetting cart data
        const shippingThreshold = approachingPromotions.get().find(
          (promo: ApproachingPromotion) => promo.promotionClass === 'SHIPPING'
        )?.thresholdStatus
        if (shippingThreshold) localStorage.setItem('shippingThreshold', shippingThreshold)
      }
      finally {
        log.info(`order placed: ${orderResponse.orderNumber}`)
        storeType === 'paypalPdp'
          ? await navigateTo(`/order-confirmation?storeContext=${storeType}`)
          : await navigateTo('/order-confirmation')
        cart.reset()
        loyaltyAutoApplyHandled.value = false
      }
    }
    catch (err: any) {
      assertApiError(err)
      // if errorId === ORD430
      if (err.errorId === ApiErrorCode.ITEM_NO_LONGER_AVAILABLE) {
        // retrieve the cart to update the cart items, totals, etc.
        await cart.get()
        // handle flash messages in errorDetails. should be after cart.get() to avoid overriding the flash messages
        const flashMessages = err.response?._data?.errorDetails?.[0]?.message || []
        if (flashMessages.length) {
          cart.handleFlashMessages(flashMessages, false)
        }
      }
      else {
        // Field-remediation errors get a sticky top banner instead of a transient toast.
        let showToast = true
        if ([
          ApiErrorCode.CC_EXPIRY_DATE_INVALID,
          ApiErrorCode.PAYMENT_INSTRUMENT_NOT_FOUND,
          ApiErrorCode.ADDRESS_NOT_FOUND,
          ApiErrorCode.PAYMENT_ERROR,
          ApiErrorCode.PAYMENT_FRAUD_VERIFICATION_EXCEPTION,
          ApiErrorCode.CART_NOT_FOUND,
          ApiErrorCode.SHIPPING_POBOX_UNAVAILABLE,
          ApiErrorCode.SOME_PRODUCTS_NO_INVENTORY
        ].includes(err.errorId)) {
          showToast = false
        }

        // Flash-type errorDetails ride on the error response - resync cart so flash handler surfaces them.
        if (err.response?._data?.errorDetails?.[0]?.message?.some?.((item: FlashError) => item._type === 'flash')) {
          showToast = false
          await cart.get()
        }

        if (showToast) {
          toast.add({
            autoClose: false,
            props: {
              message: err.message,
              type: 'error'
            }
          })
        }
        else {
          cart.setNotification('error', err.message, 'top')
        }
      }
    }
    finally {
      isOrderProcessing.value = false
    }
  }
  /**
   * Resume a 3DS2-pending order with the challenge result. Picks the payload shape from the
   * earlier flash message (`redirect` → wrap as `{ details: { redirectResult } }`, otherwise
   * forward `threeDS2Result.data` verbatim) so Adyen / vendor can re-evaluate authorization.
   * On second failure, recreates an empty cart so the shopper isn't stuck on a poisoned basket.
   * On success, normalizes via `success()`, navigates to /order-confirmation, and resets cart +
   * shippingAddress.
   * @param threeDS2Result - the challenge result from the SDK / redirect callback
   * @param code - which redirect/challenge-capable payment method is being completed
   * @returns true on authorized, false on second failure
   */
  async function patchOrder(
    threeDS2Result,
    code: Extract<PaymentMethodCode, 'BANCONTACT' | 'CREDIT_CARD' | 'IDEAL'>,
    recreateBasketOnFailure = true
  ) {
    isOrderProcessing.value = true
    try {
      const threeDSType = order.value.flash?.[0]?.message
      const additionalData = order.value.paymentMethod.find((method) => method.code === code)?.additionalData
      const patchResponse = await api.cart.$patchOrder(order.value.orderNumber, {
        paymentMethod: {
          code,
          additionalData: [
            {
              cardType: additionalData?.card_type,
              paymentInstrumentId: additionalData?.payment_instrument_id,
              sessionID: forterSessionToken.value || $deviceFingerPrint.getSessionId(),
              threeDS2Result: threeDSType === 'redirect'
                ? JSON.stringify({
                    details: { redirectResult: threeDS2Result }
                  })
                : JSON.stringify(threeDS2Result.data)
            }
          ]
        }
      })

      if (!isAuthorized(patchResponse)) {
        cart.setNotification('error', $t.paymentAuthenticationError, 'top')
        resetOrder()
        // skip basket recreation when caller handles restoration via cart.get() → recreateCart()
        if (recreateBasketOnFailure)
          cart.basketId = (await api.cart.$create({})).id!
        return false
      }

      try {
        success(patchResponse) // prepare data for confirmation page
        return true
      }
      finally {
        log.info(`order placed with 3DS Challenge: ${patchResponse.orderNumber}`)
        await navigateTo('/order-confirmation')
        // on successful order reset references we don't want to keep
        cart.reset()
        shippingAddress.value = undefined
      }
    }
    catch (err: any) {
      cart.setNotification('error', err.message, 'top')
      assertApiError(err)
      resetOrder()
      // skip basket recreation when caller handles restoration via cart.get() → recreateCart()
      if (recreateBasketOnFailure)
        cart.basketId = (await api.cart.$create({})).id!
    }
    finally {
      isOrderProcessing.value = false
    }
  }

  /**
   * Pre-validate the cart on the server before showing the order summary / placing the order.
   * Hydrates cart from the response (without emitting the GTM update event) and re-syncs totals
   * to catch any drift between the cart state and what the OMS actually committed. Silently
   * ignores `CART_NOT_FOUND` because the cart store recreates it transparently.
   */
  async function prepareOrder() {
    try {
      const orderPrepareResponse: any = await api.cart.$prepareOrder({
        basketId: cart.basketId
      }, storeType === 'applePayPdp' || storeType === 'paypalPdp' ? { bid: 'cookie' } : undefined)
      cart.updateCartData(orderPrepareResponse, {
        emitUpdateEvent: false,
        keepNotifications: !cart.notification.message
      })
      // make sure we re-sync the order totals with the cart totals in case there were changes in the meantime
      cart.totals = {
        ...orderPrepareResponse.totals,
        totalDiscountPercentage:
          getPercentage(orderPrepareResponse.totals.itemTotal, orderPrepareResponse.totals.totalDiscount)
      }
    }
    catch (err) {
      assertApiError(err)
      // don't show error notification if cart was not found as it will be recreated silently
      if (err?.errorId !== ApiErrorCode.CART_NOT_FOUND)
        cart.setNotification('error', err.message, 'top')
    }
  }

  return {
    billingAddressRef,
    bin,
    isOrderFinalized,
    isOrderProcessing,
    isExpressCheckout,
    order,
    placeOrder,
    patchOrder,
    prepareOrder,
    resetOrder,
    shippingAddress,
    success,
    threeDS2Challenge,
    paymentProvider,
    loyaltyAutoApplyHandled,
  }
}, {
  // Persist the placed order across reloads so /order-confirmation survives a refresh, and
  // keep the loyalty auto-apply latch so we don't re-apply on bounce-back to the cart page.
  persist: {
    pick: ['order', 'loyaltyAutoApplyHandled']
  }
})()
